/**
 * Environment configuration and detection utilities
 * Provides centralized environment management for the application
 */

export type Environment = 'development' | 'production' | 'test';

/**
 * Get the current environment
 */
export function getEnvironment(): Environment {
  // First check explicit environment variable
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV as Environment;
  if (appEnv && ['development', 'production', 'test'].includes(appEnv)) {
    return appEnv;
  }

  // Fallback to NODE_ENV
  const nodeEnv = process.env.NODE_ENV;
  if (nodeEnv === 'production') return 'production';
  if (nodeEnv === 'test') return 'test';
  return 'development';
}

/**
 * Check if we're in development environment
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Check if we're in production environment
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Check if we're in test environment
 */
export function isTest(): boolean {
  return getEnvironment() === 'test';
}

/**
 * Check if debug mode is enabled (only in development)
 */
export function isDebugMode(): boolean {
  return isDevelopment() && process.env.NEXT_PUBLIC_DEBUG_MODE === 'true';
}

/**
 * Get the Firebase project ID based on environment
 * Uses client-side variable but validates it
 * Returns null during build phase when environment variables might not be available
 */
export function getFirebaseProjectId(): string | null {
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  if (!projectId) {
    // During build phase, environment variables might not be available
    // Return null instead of throwing to prevent build failures
    if (process.env.NODE_ENV === undefined || process.env.NEXT_PHASE === 'phase-production-build') {
      return null;
    }
    console.warn('Firebase project ID not configured');
    return null;
  }

  // Validate project ID format (basic security check)
  if (!/^[a-z0-9-]+$/i.test(projectId)) {
    console.warn('Invalid Firebase project ID format');
    return null;
  }

  return projectId;
}

/**
 * Check if Firebase emulators should be used
 */
export function shouldUseFirebaseEmulators(): boolean {
  return isDevelopment() && process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === 'true';
}

/**
 * Check if Firebase Admin emulators should be used
 */
export function shouldUseFirebaseAdminEmulators(): boolean {
  return isDevelopment() && process.env.USE_FIREBASE_ADMIN_EMULATOR === 'true';
}

/**
 * Get API base URL
 */
export function getApiBaseUrl(): string {
  return process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3000';
}

/**
 * Environment configuration object
 * Some values may be null during build phase
 */
export const env = {
  environment: getEnvironment(),
  isDevelopment: isDevelopment(),
  isProduction: isProduction(),
  isTest: isTest(),
  isDebugMode: isDebugMode(),
  firebaseProjectId: getFirebaseProjectId(),
  shouldUseFirebaseEmulators: shouldUseFirebaseEmulators(),
  shouldUseFirebaseAdminEmulators: shouldUseFirebaseAdminEmulators(),
  apiBaseUrl: getApiBaseUrl(),
} as const;

// Log environment info in development
if (isDevelopment() && typeof window !== 'undefined') {
  console.log('🌍 Environment Configuration:', {
    environment: env.environment,
    firebaseProjectId: env.firebaseProjectId,
    useEmulators: env.shouldUseFirebaseEmulators,
    debugMode: env.isDebugMode,
  });
}
